export const DEFAULT_DOCUMENT_TITLE = "Goldsand";
export const DEFAULT_DOCUMENT_DESCRIPTION =
  "A high-yield alternative to savings accounts. Earn 5%+ on your cash savings. No interest. Pure profit-sharing.";

export interface DocumentMeta {
  title: string;
  description: string;
  robots?: string;
}

const ROUTE_META: Record<string, DocumentMeta> = {
  "/": {
    title: DEFAULT_DOCUMENT_TITLE,
    description: DEFAULT_DOCUMENT_DESCRIPTION,
  },
  "/export-wallet": {
    title: "Export wallet · Goldsand",
    description: "Securely export your Goldsand wallet from the mobile app.",
    robots: "noindex",
  },
  "/download": {
    title: "Download Goldsand",
    description: "Download Goldsand to start saving and earning.",
  },
  "/support": {
    title: "Goldsand Support",
    description: "Get help from Goldsand support for your account, wallet, or app experience.",
  },
};

const UNIVERSAL_LINK_ROUTE_META: Record<string, DocumentMeta> = {
  "/plaid-oauth": {
    title: "Finish linking your bank · Goldsand",
    description: "Open Goldsand to finish linking your bank account with Plaid.",
    robots: "noindex",
  },
  "/return-to-app": {
    title: "Returning to Goldsand",
    description: "If you're not sent back automatically, tap the button below.",
    robots: "noindex",
  },
  "/auth/invite-code": {
    title: "Download Goldsand",
    description: "Open Goldsand to claim your invite, or download the app to get started.",
    robots: "noindex",
  },
};

export function normalizePathname(pathname: string) {
  if (pathname === "") return "/";
  if (pathname !== "/" && pathname.endsWith("/")) return pathname.slice(0, -1);
  return pathname;
}

export function isUniversalLinkAppPath(pathname: string) {
  return pathname === "/app" || pathname.startsWith("/app/");
}

const INVITE_CODE_PATH_PATTERN = /^\/i\/([^/]+)\/?$/;

export function isInviteCodePath(pathname: string) {
  return INVITE_CODE_PATH_PATTERN.test(pathname);
}

export function inviteCodeFromPath(pathname: string): string | null {
  const match = pathname.match(INVITE_CODE_PATH_PATTERN);
  if (!match) return null;
  let decoded: string;
  try {
    decoded = decodeURIComponent(match[1]);
  } catch {
    // Malformed percent-encoding (e.g. "/i/%") — treat as no code.
    return null;
  }
  const code = decoded.trim().toUpperCase();
  return code.length > 0 ? code : null;
}

// Title personalizes to "{firstName} invited you to Goldsand" when the
// referrer's name is known; otherwise falls back to the generic invite copy.
// noindex: invite URLs are per-user and must not be indexed by search engines
// (link-preview crawlers ignore robots, so unfurls still work).
export function inviteCodeMeta(firstName?: string | null): DocumentMeta {
  return {
    title: firstName ? `${firstName} invited you to Goldsand` : "You're invited to Goldsand",
    description: "You've been invited to join Goldsand - the halal savings account.",
    robots: "noindex",
  };
}

export function documentMetaForPath(pathname: string): DocumentMeta {
  const normalized = normalizePathname(pathname);

  if (isUniversalLinkAppPath(normalized)) {
    const appPath = normalizePathname(normalized.slice("/app".length) || "/");
    return UNIVERSAL_LINK_ROUTE_META[appPath] ?? {
      title: "Goldsand App",
      description: DEFAULT_DOCUMENT_DESCRIPTION,
    };
  }

  if (isInviteCodePath(normalized)) {
    return inviteCodeMeta();
  }

  return ROUTE_META[normalized] ?? {
    title: "Page not found · Goldsand",
    description: "The Goldsand page you're looking for could not be found.",
  };
}

export function documentTitleForPath(pathname: string) {
  return documentMetaForPath(pathname).title;
}
